import { ChevronLeft, ChevronRight, Download, Minus, Plus, Printer, UsersRound, CheckCircle2, AlertTriangle } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Button } from '../../components/shared'
import { exportRecordsCsv } from '../../utils/export'
import { safetyStatusLabel } from '../../data/safetyChecks'

const PAGE_SIZE = 15

function countWeekdays(month) {
  const [year, monthNumber] = month.split('-').map(Number)
  const daysInMonth = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate()
  return Array.from({ length: daysInMonth }, (_, index) => index + 1).filter((day) => {
    const weekday = new Date(Date.UTC(year, monthNumber - 1, day)).getUTCDay()
    return weekday >= 1 && weekday <= 5
  }).length
}

export default function MonthlyReport({ drivers, vehicles, records, settings, defaultMonth = '2026-07', createdDateJa = '2026年7月18日' }) {
  const [month, setMonth] = useState(defaultMonth)
  const [page, setPage] = useState(1)
  const [zoom, setZoom] = useState(100)
  const driverMap = Object.fromEntries(drivers.map((item) => [item.id, item.name]))
  const vehicleMap = Object.fromEntries(vehicles.map((item) => [item.id, item.number]))
  const monthRecords = useMemo(() => records.filter((record) => record.date.startsWith(month)).sort((a, b) => b.createdAt.localeCompare(a.createdAt)), [records, month])
  const totalPages = Math.max(1, Math.ceil(monthRecords.length / PAGE_SIZE))
  const visibleRecords = monthRecords.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const printPages = useMemo(() => Array.from({ length: totalPages }, (_, index) => monthRecords.slice(index * PAGE_SIZE, (index + 1) * PAGE_SIZE)), [monthRecords, totalPages])
  const reviewCount = monthRecords.filter((record) => record.status === 'review').length
  const activeDriverCount = drivers.filter((item) => item.active).length
  const expectedCheckCount = activeDriverCount * countWeekdays(month) * 2
  const rate = expectedCheckCount ? Math.min(100, (monthRecords.length / expectedCheckCount) * 100).toFixed(1) : '0.0'
  const beforeWorkCount = monthRecords.filter((record) => record.phase === '業務前').length
  const afterWorkCount = monthRecords.filter((record) => record.phase === '業務後').length
  const monthJa = `${month.split('-')[0]}年${Number(month.split('-')[1])}月`

  const changeMonth = (value) => {
    if (!value) return
    setMonth(value)
    setPage(1)
  }

  return (
    <>
      <div className="report-toolbar">
        <input type="month" value={month} onInput={(event) => changeMonth(event.currentTarget.value)} aria-label="対象年月" />
        <div><Button icon={Download} variant="secondary" onClick={() => exportRecordsCsv(monthRecords, drivers, vehicles, `アルコール検知器使用記録簿_${month}.csv`)}>CSV出力</Button><Button icon={Printer} onClick={() => window.print()}>印刷・PDF保存</Button></div>
      </div>
      <section className="summary-band report-summary">
        <div className="summary-item summary-item--neutral"><UsersRound /><span><small>対象ドライバー</small><strong>{activeDriverCount}<em>名</em></strong></span></div>
        <div className="summary-item summary-item--completed"><CheckCircle2 /><span><small>実施率</small><strong>{rate}<em>%</em></strong></span></div>
        <div className="summary-item summary-item--review"><AlertTriangle /><span><small>要確認</small><strong>{reviewCount}<em>件</em></strong></span></div>
      </section>
      <div className="report-layout screen-report-layout">
        <div className="report-canvas">
          <ReportSheet companyName={settings.companyName} monthJa={monthJa} createdDateJa={createdDateJa} records={visibleRecords} driverMap={driverMap} vehicleMap={vehicleMap} page={page} totalPages={totalPages} zoom={zoom} />
          <div className="report-pagination">
            <button aria-label="前のページ" disabled={page === 1} onClick={() => setPage((value) => value - 1)}><ChevronLeft /></button><span>{page} / {totalPages}</span><button aria-label="次のページ" disabled={page === totalPages} onClick={() => setPage((value) => value + 1)}><ChevronRight /></button>
            <i />
            <button aria-label="縮小" onClick={() => setZoom((value) => Math.max(70, value - 10))}><Minus /></button><span>{zoom}%</span><button aria-label="拡大" onClick={() => setZoom((value) => Math.min(120, value + 10))}><Plus /></button>
          </div>
        </div>
        <aside className="report-settings">
          <h3>報告書設定</h3>
          <dl><div><dt>会社名</dt><dd>{settings.companyName}</dd></div><div><dt>対象年月</dt><dd>{monthJa}</dd></div><div><dt>記録件数</dt><dd>{monthRecords.length}件（業務前 {beforeWorkCount}／業務後 {afterWorkCount}）</dd></div></dl>
          <label>出力形式<select defaultValue="A4横"><option>A4横</option><option>A4縦</option></select></label>
          <label>表示項目<select defaultValue="すべての項目"><option>すべての項目</option><option>法定項目のみ</option></select></label>
          <label>並び順<select defaultValue="日時（降順）"><option>日時（降順）</option><option>日時（昇順）</option><option>運転者名</option></select></label>
          <p>数値は任意の運用証跡項目です。酒気帯びの有無、確認方法、指示事項を別項目として出力します。</p>
        </aside>
      </div>
      <div className="print-report-stack" aria-hidden="true">
        {printPages.map((pageRecords, index) => (
          <ReportSheet
            key={`${month}-${index + 1}`}
            companyName={settings.companyName}
            monthJa={monthJa}
            createdDateJa={createdDateJa}
            records={pageRecords}
            driverMap={driverMap}
            vehicleMap={vehicleMap}
            page={index + 1}
            totalPages={totalPages}
            zoom={100}
          />
        ))}
      </div>
    </>
  )
}

function ReportSheet({ companyName, monthJa, createdDateJa, records, driverMap, vehicleMap, page, totalPages, zoom }) {
  return (
    <article className="report-sheet print-sheet" style={{ '--report-zoom': zoom / 100 }}>
      <header><span>{companyName}</span><span>対象：{monthJa}</span></header>
      <h2>アルコール検知器使用記録簿</h2>
      <table>
        <thead><tr><th>日付</th><th>時刻</th><th>区分</th><th>運転者名</th><th>車両番号</th><th>安全点呼</th><th>確認方法</th><th>測定値</th><th>酒気</th><th>確認者・指示事項</th></tr></thead>
        <tbody>{records.map((record) => <tr key={record.id} className={record.status === 'review' ? 'report-row-review' : ''}><td>{`${Number(record.date.slice(5, 7))}月${Number(record.date.slice(8, 10))}日`}</td><td>{record.time}</td><td>{record.phase}</td><td>{driverMap[record.driverId]}</td><td>{vehicleMap[record.vehicleId]}</td><td>{safetyStatusLabel(record.preDriveCheck)}{record.preDriveCheck?.note ? <small>{record.preDriveCheck.note}</small> : null}</td><td>{record.confirmationMethod}<small>{record.method}</small></td><td>{Number(record.value).toFixed(2)} mg/L</td><td>{record.confirmationMethod === '未確認' ? '未確認' : record.presence ? '有' : '無'}</td><td>{record.confirmer}<small>{record.instruction}</small></td></tr>)}</tbody>
      </table>
      {!records.length ? <div className="report-empty">対象期間の記録はありません</div> : null}
      <footer><span>作成日：{createdDateJa}</span><span>{page} / {totalPages}</span></footer>
    </article>
  )
}
